import { CSSInterpolation } from '@emotion/css/create-instance'
import { cx } from '@emotion/css'
import { css } from '@emotion/react'
import React, { type ReactNode, type Ref, useContext } from 'react'

import type { ReactAnchorProps, ReactButtonProps } from '../../types'
import {
  type ColorToken,
  CssVars,
  NeutralColorToken,
  SemanticColorToken,
  type ThemeColorToken,
} from '../themes'
import { useIsAchromaticTheme } from './AchromaticThemeOverride'
import { BaseButton } from './BaseButton'
import { FoundationIcon } from './FoundationIcon'
import type { Icon16 } from './FoundationIcon/icons'
import {
  ForegroundColorPrimaryHoverState,
  LowOpacityHoverState,
} from './HoverState'
import { InternalButtonContent } from './internal/InternalButtonContent'
import { PrimaryColorOverride } from './PrimaryColorOverride'
import { Spinner16 } from './Spinner'
import { TextColorOverride } from './TextColorOverride'
import { getColorValue, useTheme } from './ThemeProvider'
import { Type, TypeContext } from './Type'

type BaseProps = {
  /**
   * @deprecated
   *
   * New TextButtons should use the new style: underlined and monochromatic color scheme. This
   * prop is used for migration purposes only and should NOT be used for new usages.
   */
  isLegacyStyle?: boolean
  state?: 'disabled' | 'loading' | false
  icon?: {
    type: Icon16
    side: 'start' | 'end'
  }

  color?: SemanticColorToken | ThemeColorToken

  /**
   * If you want to clamp your text to a particular number of lines, specify the number here
   * as a positive integer. The text will be truncated after that many lines and an ellipsis
   * will be added.
   */
  maxLines?: number

  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
} & (
  | {
      variant?: 'inline'
      // Inline TextButtons use the size of the text they're inline with.
      size?: never
    }
  | {
      /**
       * Whether the `TextButton` is inline with other text or is being used as a standalone
       * button. Defaults to `'inline'`.
       */
      variant: 'standalone'
      /**
       * When using a standalone `TextButton`, you can select either `size="S"` or `size="M"`
       * for the underlying `Type.Text` component. Defaults to `"S"`. (An inline `TextButton`
       * will use the size of the text it's inline with.)
       */
      size?: 'S' | 'M'
    }
)

type OmittedElementPropKeys =
  | keyof BaseProps
  | 'ref'
  | 'disabled'
  | 'className'
  | 'style'
  | 'css'
  | 'type'

type AnchorProps = {
  type: 'anchor'
  innerRef?: Ref<HTMLAnchorElement>
} & Omit<ReactAnchorProps, OmittedElementPropKeys>

type ButtonProps = {
  type: 'button'
  innerRef?: Ref<HTMLButtonElement>
} & Omit<ReactButtonProps, OmittedElementPropKeys>

type InternalProps = BaseProps &
  (AnchorProps | ButtonProps) & { label: ReactNode }
type Props = BaseProps &
  // NOTE(felipe.brahm): Array<string> was added here to support the "chunks" string array from react-intl's FormattedMessage.
  (AnchorProps | ButtonProps) & { label: string | Array<string> }

export function TextButton(props: Props) {
  return <InternalTextButton {...props} />
}

export function getInlineButtonStyles(cssColorValue: string) {
  return css({
    textDecoration: 'underline',
    textDecorationColor: cssColorValue,
    textUnderlineOffset: 3,
  })
}

export function InternalTextButton({
  color,
  icon,
  isLegacyStyle = false,
  label,
  maxLines,
  overrideRootStyles,
  size = 'S',
  state,
  variant = 'inline',
  ...props
}: InternalProps) {
  // If this is rendered outside of a Type component or it's a standalone text button,
  // then we use the default text button text style.
  const typeContext = useContext(TypeContext)
  const theme = useTheme()
  // For legacy style mode the default is SemanticColorToken
  let defaultColor = color

  const shouldUseLegacyStyle = !useIsAchromaticTheme() && isLegacyStyle

  if (!defaultColor) {
    if (shouldUseLegacyStyle) {
      defaultColor = SemanticColorToken.Primary
    } else {
      defaultColor = NeutralColorToken.Fg1
    }
  }

  let textColor =
    shouldUseLegacyStyle || color
      ? SemanticColorToken.Primary
      : NeutralColorToken.Fg1

  if (state === 'disabled' || state === 'loading') {
    textColor = NeutralColorToken.Fg3
  }

  const content = (
    <InternalButtonContent
      // NOTE(trevor.healy): The InternalButtonContent returns only the spinner
      // if the "state" is set to "loading". In the case of the TextButton, we still
      // want the "label" property to appear so we are overriding "state" to "default".
      state={state === 'loading' ? 'default' : state}
    >
      <TextButtonAccessory
        color={shouldUseLegacyStyle ? undefined : textColor}
        icon={icon}
        side="start"
        state={state}
      />
      {variant === 'standalone' || !typeContext ? (
        <Type.Text
          color={shouldUseLegacyStyle ? undefined : textColor}
          isBold
          maxLines={maxLines}
          size={size}
        >
          {label}
        </Type.Text>
      ) : (
        <Type.Extend
          color={shouldUseLegacyStyle ? undefined : textColor}
          isBold
          maxLines={maxLines}
        >
          {label}
        </Type.Extend>
      )}
      <TextButtonAccessory
        color={shouldUseLegacyStyle ? undefined : textColor}
        icon={icon}
        side="end"
        state={state}
      />
    </InternalButtonContent>
  )

  const baseButtonRootStyles = [
    variant === 'standalone' && {
      // NOTE(jare): Add some padding to increase the clickable area, but
      // use negative margin to offset any shift in the layout.
      marginLeft: `calc(-1 * ${CssVars.size.gutterWidthXs})`,
      marginRight: `calc(-1 * ${CssVars.size.gutterWidthXs})`,
      paddingLeft: CssVars.size.gutterWidthXs,
      paddingRight: CssVars.size.gutterWidthXs,
    },
    variant === 'standalone' &&
      shouldUseLegacyStyle && {
        height: 28,
      },
    !shouldUseLegacyStyle &&
      getInlineButtonStyles(getColorValue(textColor, theme)),
    overrideRootStyles,
  ]

  if (!shouldUseLegacyStyle && !color) {
    return (
      <ForegroundColorPrimaryHoverState>
        {({ hoverableClassName }) => (
          <BaseButton
            className={hoverableClassName}
            state={state}
            {...props}
            overrideRootStyles={baseButtonRootStyles}
          >
            {content}
          </BaseButton>
        )}
      </ForegroundColorPrimaryHoverState>
    )
  }

  return (
    <PrimaryColorOverride color={defaultColor}>
      {({ rootClassName: primaryColorClassName }) => (
        <LowOpacityHoverState>
          {({ hoverableClassName }) => (
            <TextColorOverride color={textColor}>
              {({ rootClassName: textColorClassName }) => (
                <BaseButton
                  className={cx(
                    primaryColorClassName,
                    hoverableClassName,
                    textColorClassName
                  )}
                  overrideRootStyles={baseButtonRootStyles}
                  state={state}
                  {...props}
                >
                  {content}
                </BaseButton>
              )}
            </TextColorOverride>
          )}
        </LowOpacityHoverState>
      )}
    </PrimaryColorOverride>
  )
}

function TextButtonAccessory({
  color,
  icon,
  side,
  state,
}: {
  icon: React.ComponentProps<typeof TextButton>['icon']
  state: React.ComponentProps<typeof TextButton>['state']
  side: 'start' | 'end'
  color?: ColorToken
}) {
  if (state === 'loading') {
    if (icon?.side === 'start' && side === 'start') {
      return <TextButtonSpinner color={color} side="start" />
    }
    if (icon?.side !== 'start' && side === 'end') {
      return <TextButtonSpinner color={color} side="end" />
    }
  }

  if (icon?.side === side) {
    return <TextButtonIcon color={color} icon={icon.type} side={icon.side} />
  }

  return null
}

function TextButtonAccessoryLayout({
  children,
  side,
}: {
  side: 'start' | 'end'
  children: React.ReactNode
}) {
  return (
    <span
      css={[
        { display: 'flex' },
        side === 'start'
          ? { marginRight: CssVars.size.gutterWidthXs }
          : { marginLeft: CssVars.size.gutterWidthXs },
      ]}
    >
      {children}
    </span>
  )
}

function TextButtonIcon({
  color,
  icon,
  side,
}: {
  icon: Icon16
  side: 'start' | 'end'
  color?: ColorToken
}) {
  return (
    <TextButtonAccessoryLayout side={side}>
      <FoundationIcon color={color} type={icon} />
    </TextButtonAccessoryLayout>
  )
}

function TextButtonSpinner({
  color,
  side,
}: {
  side: 'start' | 'end'
  color?: ColorToken
}) {
  return (
    <TextButtonAccessoryLayout side={side}>
      <Spinner16 color={color} overrideRootStyles={{ height: 13, width: 13 }} />
    </TextButtonAccessoryLayout>
  )
}
